import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, Signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ProfileAction } from '@app/layout/common/profile/state/profile.action';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { Profile } from '@app/layout/common/profile/state/profile.types';
import { TranslocoPipe } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';

@Component({
  selector: 'app-initial-popup',
  standalone: true,
  imports: [BoxComponent, HeaderComponent, BodyComponent, TextboxComponent, TranslocoPipe, FooterComponent],
  styles: [
    `
      .mat-mdc-dialog-container .mdc-dialog__surface {
        padding: 0;
      }
    `
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <box>
      <app-header [title]="'setting.profile.update_email' | transloco" />
      <app-body>
        <textbox
          [hasPadding]="true"
          [label]="'setting.profile.email_login' | transloco"
          [placeholder]="'setting.profile.email_login_placeholder' | transloco"
          class="col-span-2 w-full"
          [showError]="true"
          [errors]="{ required: 'system.required' | transloco, email: 'system.email' | transloco }"
          [control]="mailForm.controls.email" />
      </app-body>
      <app-footer [disabledSaveButton]="mailForm.invalid" (save)="onUpdate()" />
    </box>
  `
})
export class InitialPopupComponent implements OnInit {
  protected readonly dialogRef: MatDialogRef<any> = inject(MatDialogRef);
  protected readonly formBuilder: FormBuilder = inject(FormBuilder);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly profileState: ProfileState = inject(ProfileState);
  protected readonly profile: Signal<Profile> = computed(() => this.profileState.profile());
  protected readonly profileAction: ProfileAction = inject(ProfileAction);
  protected readonly mailForm: FormGroup<{
    email: FormControl<string | null>;
  }> = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]]
  });

  ngOnInit() {
    this.mailForm.patchValue(this.profileState.profile());
  }

  onUpdate() {
    this.profileAction.actions.updateProfile({
      id: this.profile()?.id?.toString(),
      profile: {
        ...this.profileState.profile(),
        email: this.mailForm.value.email
      }
    });
    this.profileAction.actions.updateProfileSuccess$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onClose();
    });
  }

  onClose() {
    this.dialogRef.close();
  }
}
